function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

import React, { useContext } from "react";
import PropTypes from "prop-types";
import { LinkContext } from "../providers/LinkProvider";
import useTheme from "../hooks/useTheme";
import useBackground, { mapResponsiveValues } from "../hooks/useBackground";
import { responsiveMarginType, responsiveWidthType } from "../hooks/useResponsiveProp";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { responsiveMargin, responsiveSize } from "../utils/css";
import { mergeProps } from "../utils/component";
import { formatArray } from "../utils/array";
import { hasOwnProperty } from "../utils/core";
import { jsx as ___EmotionJSX } from "@emotion/core";
var APPEARANCES = ["text", "primary-button", "secondary-button", "icon"];
var VARIANTS = ["light-bg", "medium-bg", "dark-bg", "blue-button", "white-button", "black-button", "green-button"];
var DEFAULT_PROPS = {
  appearance: "text",
  variant: "light-bg",
  __internal__keyboardFocus: false,
  __internal__hover: false,
  __internal__active: false
};
Link.APPEARANCES = APPEARANCES;
Link.VARIANTS = VARIANTS;
Link.DEFAULT_PROPS = DEFAULT_PROPS;
var darkColorsMap = {
  "primary.blue.t100": true,
  "highlight.blue.t100": true,
  "highlight.pink.t100": true,
  "highlight.purple.t100": true
};
var mediumColorsMap = {
  "grey.t07": true,
  "grey.t10": true,
  "grey.t16": true,
  "secondary.lightBlue.t25": true,
  "secondary.pink.t30": true,
  "secondary.pink.t15": true,
  "secondary.purple.t30": true,
  "secondary.purple.t15": true,
  "secondary.turquoise.t30": true,
  "secondary.turquoise.t10": true
};

function Link(props) {
  var theme = useTheme();

  var _useBackground = useBackground(),
      bgMap = _useBackground.bgMap;

  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    appearance: function appearance(_appearance) {
      return APPEARANCES.includes(_appearance);
    },
    variant: function variant(_variant) {
      return VARIANTS.includes(_variant);
    },
    newTab: function newTab(_newTab) {
      return typeof _newTab === "boolean";
    },
    title: function title(_title) {
      return typeof _title === "string";
    }
  });
  var appearance = mergedProps.appearance,
      href = mergedProps.href,
      newTab = mergedProps.newTab,
      title = mergedProps.title,
      state = mergedProps.state,
      onClick = mergedProps.onClick,
      children = mergedProps.children,
      testId = mergedProps.testId,
      __internal__keyboardFocus = mergedProps.__internal__keyboardFocus,
      __internal__hover = mergedProps.__internal__hover,
      __internal__active = mergedProps.__internal__active;
  var variantMap = mapResponsiveValues(bgMap, function (backgroundColor) {
    var isDarkBackground = darkColorsMap[backgroundColor] === true;
    var isMediumBackground = mediumColorsMap[backgroundColor] === true;

    if (appearance === "text") {
      return isDarkBackground ? "dark-bg" : isMediumBackground ? "medium-bg" : "light-bg";
    }

    if (appearance === "primary-button") {
      return isDarkBackground ? "white-button" : "blue-button";
    }

    if (appearance === "secondary-button") {
      return isDarkBackground ? "white-button" : isMediumBackground ? "black-button" : "blue-button";
    }

    return null;
  }, theme);
  var anchorCSS = useResponsivePropsCSS(mergedProps, DEFAULT_PROPS, {
    variant: function variant(_, theme) {
      var appearance = mergedProps.appearance;
      return theme.link.getCSS({
        targetElement: "anchor",
        appearance: appearance,
        __internal__keyboardFocus: __internal__keyboardFocus
      });
    },
    margin: responsiveMargin,
    width: responsiveSize("width")
  });
  var spanCSS = useResponsivePropsCSS(mergedProps, DEFAULT_PROPS, {
    variant: function variant(_, theme, bp) {
      var appearance = mergedProps.appearance;
      var variant = hasOwnProperty(props, "variant") ? mergedProps.variant : variantMap[bp];
      return theme.link.getCSS({
        targetElement: "span",
        appearance: appearance,
        variant: variant,
        buttonTheme: theme.button,
        __internal__keyboardFocus: __internal__keyboardFocus,
        __internal__hover: __internal__hover,
        __internal__active: __internal__active
      });
    }
  });

  var _useContext = useContext(LinkContext),
      InternalLink = _useContext.InternalLink,
      isLinkInternal = _useContext.isLinkInternal;

  var newTabProps = newTab ? {
    target: "_blank",
    rel: "noreferrer" // See: https://developers.google.com/web/tools/lighthouse/audits/noopener, https://html.spec.whatwg.org/multipage/links.html#link-type-noreferrer

  } : {};

  if (!newTab && InternalLink && isLinkInternal(href)) {
    /*
      Note: We assume here that InternalLink respects the following contract:
         - It gets a `state` prop
        - It gets a `to` prop, which gets mapped to <a>'s `href` prop.
        - It gets the following props and sets them on the rendered <a>:
          * title
          * onClick
          * data-testid
          * children
       Example: Gatsby `Link` component.
    */
    return ___EmotionJSX(InternalLink, {
      css: anchorCSS,
      to: href,
      title: title,
      state: state,
      onClick: onClick,
      "data-testid": testId
    }, ___EmotionJSX("span", {
      css: spanCSS
    }, children));
  }

  return ___EmotionJSX("a", _extends({
    css: anchorCSS,
    href: href,
    title: title,
    onClick: onClick,
    "data-testid": testId
  }, newTabProps), ___EmotionJSX("span", {
    css: spanCSS
  }, children));
}

Link.propTypes = _objectSpread(_objectSpread(_objectSpread({}, responsiveMarginType), responsiveWidthType), {}, {
  appearance: function appearance(props) {
    if (props.appearance === undefined) {
      return;
    }

    if (APPEARANCES.includes(props.appearance) === false) {
      return new Error("Link: appearance=\"".concat(props.appearance, "\" is not supported. Must be one of: ").concat(formatArray(APPEARANCES)));
    }

    if (props.appearance === "text" && props.variant && ["light-bg", "medium-bg", "dark-bg"].includes(props.variant) === false) {
      return new Error("Link: appearance=\"text\" should be used only with these variants: ".concat(formatArray(["light-bg", "medium-bg", "dark-bg"])));
    }

    if (props.appearance === "primary-button" && props.variant && ["blue-button", "white-button", "green-button"].includes(props.variant) === false) {
      return new Error("Link: appearance=\"primary-button\" should be used only with these variants: ".concat(formatArray(["blue-button", "white-button", "green-button"])));
    }

    if (props.appearance === "secondary-button" && props.variant && ["blue-button", "white-button", "black-button"].includes(props.variant) === false) {
      return new Error("Link: appearance=\"secondary-button\" should be used only with these variants: ".concat(formatArray(["blue-button", "white-button", "black-button"])));
    }

    if (["primary-button", "secondary-button"].includes(props.appearance) === false) {
      for (var prop in props) {
        if (prop.startsWith("width")) {
          return new Error("Link: ".concat(prop, " should be used only with these appearances: ").concat(formatArray(["primary-button", "secondary-button"])));
        }
      }
    }
  },
  variant: PropTypes.oneOf(VARIANTS),
  href: PropTypes.string.isRequired,
  newTab: PropTypes.bool.isRequired,
  title: PropTypes.string,
  state: PropTypes.object,
  onClick: PropTypes.func,
  children: PropTypes.node,
  testId: PropTypes.string,
  __internal__keyboardFocus: PropTypes.bool,
  __internal__hover: PropTypes.bool,
  __internal__active: PropTypes.bool
});
export default Link;